/** Eye state: accommodation, pupil, acuity floor, Stiles–Crawford (docs/OPTICS.md §2, §9). */

export type Lighting = 'day' | 'indoor' | 'chart' | 'night';

const AMPLITUDE_TABLE: ReadonlyArray<readonly [number, number]> = [
  [10, 14], [20, 11], [30, 8], [40, 5], [45, 3], [50, 1.5], [55, 0.75], [60, 0.25], [65, 0],
];

/** Objective accommodation amplitude A(age), D. Linear interpolation, flat outside the table. */
export function accommodationAmplitude(age: number): number {
  const t = AMPLITUDE_TABLE;
  const first = t[0]!;
  const last = t[t.length - 1]!;
  if (age <= first[0]) return first[1];
  if (age >= last[0]) return last[1];
  for (let i = 1; i < t.length; i++) {
    const [a1, v1] = t[i]!;
    if (age <= a1) {
      const [a0, v0] = t[i - 1]!;
      return v0 + ((v1 - v0) * (age - a0)) / (a1 - a0);
    }
  }
  return last[1];
}

const PUPIL_TABLE: Record<Lighting, { base: number; k: number }> = {
  day: { base: 3.2, k: 0.012 },
  indoor: { base: 4.2, k: 0.02 },
  chart: { base: 3.8, k: 0.016 },
  night: { base: 6.6, k: 0.042 },
};

/** Pupil diameter for a lighting condition and age, mm. */
export function pupilDiameter(lighting: Lighting, age: number): number {
  const { base, k } = PUPIL_TABLE[lighting];
  return Math.max(2.0, base - k * Math.max(0, age - 25));
}

/** Neural/optical acuity floor MAR0, arcmin. */
export function mar0(age: number): number {
  return age <= 40 ? 0.8 : 0.8 + 0.008 * (age - 40);
}

/** Stiles–Crawford apodization weight for a ray at radius r (mm) from the pupil centre. */
export function sceWeight(r: number): number {
  return Math.pow(10, -0.05 * r * r);
}

/** Reading add of progressive glasses by age, D (OPTICS §6). */
export function progressiveAddByAge(age: number): number {
  if (age < 40) return 0;
  if (age < 45) return 1.0;
  if (age >= 60) return 2.5;
  // 45 → 1.0, 50 → 1.5, 55 → 2.0, 60 → 2.5: +0.5 D per 5 years.
  const add = 1.0 + ((age - 45) / 5) * 0.5;
  return Math.round(add * 4) / 4;
}
